// File introduced by SOPTIM, 2026

import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Header,
  Pagination,
  Table,
  TextFilter,
  SpaceBetween,
  CollectionPreferences,
  Modal,
  FormField,
  ButtonDropdown,
  Form,
  ColumnLayout,
  Input,
} from "@awsui/components-react";
import { DatePicker } from "antd";
import moment from "moment";
import { useCollection } from "@awsui/collection-hooks";
import {
  getAllMaintenance,
  addMaintenance,
  delMaintenance,
  editMaintenance,
  revokeConflictingRequests,
  detectMaintenanceOverlap,
} from "../Shared/RequestService";
import "../../index.css";
import { validateMaintenance } from "./maintenanceValidation";

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function formatDateTime(dt) {
  if (!dt) return "-";
  return moment(dt).format("YYYY-MM-DD HH:mm");
}

const COLUMN_DEFINITIONS = [
  { id: "id",        sortingField: "id",        header: "Id",         cell: (item) => item.id,                         width: 220 },
  { id: "startTime", sortingField: "startTime", header: "Start time", cell: (item) => formatDateTime(item.startTime), width: 180 },
  { id: "endTime",   sortingField: "endTime",   header: "End time",   cell: (item) => formatDateTime(item.endTime),   width: 180 },
  { id: "showFrom",  sortingField: "showFrom",  header: "Show from",  cell: (item) => formatDateTime(item.showFrom),  width: 180 },
  { id: "showUntil", sortingField: "showUntil", header: "Show until", cell: (item) => formatDateTime(item.showUntil), width: 180 },
  { id: "message",   sortingField: "message",   header: "Message",    cell: (item) => item.message || "-",             width: 200 },
];

const MyCollectionPreferences = ({ preferences, setPreferences }) => (
  <CollectionPreferences
    title="Preferences"
    confirmLabel="Confirm"
    cancelLabel="Cancel"
    preferences={preferences}
    onConfirm={({ detail }) => setPreferences(detail)}
    pageSizePreference={{
      title: "Page size",
      options: [
        { value: 10, label: "10 entries" },
        { value: 30, label: "30 entries" },
        { value: 50, label: "50 entries" },
      ],
    }}
    wrapLinesPreference={{ label: "Wrap lines", description: "Check to see all the text and wrap the lines" }}
    visibleContentPreference={{
      title: "Select visible columns",
      options: [
        {
          label: "Maintenance properties",
          options: [
            { id: "id",        label: "Id" },
            { id: "startTime", label: "Start time", editable: false },
            { id: "endTime",   label: "End time",   editable: false },
            { id: "showFrom",  label: "Show from" },
            { id: "showUntil", label: "Show until" },
            { id: "message",   label: "Message" },
          ],
        },
      ],
    }}
  />
);

function EmptyState({ title, subtitle, action }) {
  return (
    <Box textAlign="center">
      <Box variant="strong">{title}</Box>
      <Box variant="p" padding={{ bottom: "s" }}>{subtitle}</Box>
      {action}
    </Box>
  );
}

const ValueWithLabel = ({ label, children }) => (
  <div>
    <Box variant="awsui-key-label">{label}</Box>
    <div>{children}</div>
  </div>
);

function DateTimeFormField({ label, description, errorText, defaultValue, onChange }) {
  const [momentVal, setMomentVal] = useState(defaultValue ? moment(defaultValue) : null);

  useEffect(() => {
    setMomentVal(defaultValue ? moment(defaultValue) : null);
  }, [defaultValue]);

  const handleChange = (val) => {
    setMomentVal(val);
    onChange(val ? val.toISOString() : null);
  };
  return (
    <FormField label={label} description={description} errorText={errorText} stretch>
      <DatePicker
        showTime
        format="YYYY-MM-DD HH:mm"
        value={momentVal}
        onChange={handleChange}
        placeholder="YYYY-MM-DD HH:mm"
      />
    </FormField>
  );
}

function MaintenanceForm({
  message, onMessageChange, messageError,
  startTimeVal, onStartTimeChange, startTimeError,
  endTimeVal, onEndTimeChange, endTimeError,
  showFromVal, onShowFromChange, showFromError,
  showUntilVal, onShowUntilChange, showUntilError,
}) {
  return (
    <SpaceBetween direction="vertical" size="l">
      <DateTimeFormField
        label="Start time"
        description="Start of the maintenance window"
        errorText={startTimeError}
        defaultValue={startTimeVal}
        onChange={onStartTimeChange}
      />
      <DateTimeFormField
        label="End time"
        description="End of the maintenance window"
        errorText={endTimeError}
        defaultValue={endTimeVal}
        onChange={onEndTimeChange}
      />
      <DateTimeFormField
        label="Show from"
        description="When to start showing the announcement banner"
        errorText={showFromError}
        defaultValue={showFromVal}
        onChange={onShowFromChange}
      />
      <DateTimeFormField
        label="Show until"
        description="When to stop showing the announcement banner"
        errorText={showUntilError}
        defaultValue={showUntilVal}
        onChange={onShowUntilChange}
      />
      <FormField label="Message" stretch description="Maintenance message displayed to users" errorText={messageError}>
        <Input
          value={message}
          onChange={({ detail }) => onMessageChange(detail.value)}
        />
      </FormField>
    </SpaceBetween>
  );
}

function Maintenance(props) {
  const [allItems, setAllItems] = useState([]);
  const [preferences, setPreferences] = useState({
    pageSize: 10,
    visibleContent: ["startTime", "endTime", "showFrom", "showUntil", "message"],
  });
  const [formKey, setFormKey] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [addVisible, setAddVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);

  const DEFAULT_MESSAGE = "Achtung: Wartungsfenster vom {startTime} bis {endTime}";
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [startTimeVal, setStartTimeVal] = useState(null);
  const [endTimeVal, setEndTimeVal] = useState(null);
  const [showFromVal, setShowFromVal] = useState(null);
  const [showUntilVal, setShowUntilVal] = useState(null);

  const [messageError, setMessageError] = useState("");
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [showFromError, setShowFromError] = useState("");
  const [showUntilError, setShowUntilError] = useState("");
  const [conflictError, setConflictError] = useState("");

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    allItems,
    {
      filtering: {
        filteringFunction: (item, filteringText) => {
          if (!filteringText) return true;
          if (DATE_REGEX.test(filteringText)) {
            const searchStart = new Date(filteringText + "T00:00:00Z");
            const searchEnd = new Date(filteringText + "T23:59:59Z");
            const inMaintenance =
              item.startTime && item.endTime &&
              new Date(item.startTime) <= searchEnd &&
              new Date(item.endTime) >= searchStart;
            const inAnnouncement =
              item.showFrom && item.showUntil &&
              new Date(item.showFrom) <= searchEnd &&
              new Date(item.showUntil) >= searchStart;
            const onSameDay = (dt) => !!dt && dt.startsWith(filteringText);
            const sameDayMatch =
              onSameDay(item.startTime) || onSameDay(item.endTime) ||
              onSameDay(item.showFrom) || onSameDay(item.showUntil);
            return inMaintenance || inAnnouncement || sameDayMatch;
          }
          const text = filteringText.toLowerCase();
          return (
            (item.message || "").toLowerCase().includes(text) ||
            (item.id || "").toLowerCase().includes(text)
          );
        },
        empty: (
          <EmptyState
            title="No maintenance entries"
            subtitle="No entries to display."
            action={<Button onClick={() => handleAddOpen()}>Add maintenance</Button>}
          />
        ),
        noMatch: (
          <EmptyState
            title="No matches"
            subtitle="Your search didn't return any records."
            action={<Button onClick={() => actions.setFiltering("")}>Clear filter</Button>}
          />
        ),
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {
        defaultState: {
          sortingColumn: { sortingField: "startTime" },
          isDescending: true,
        },
      },
      selection: {},
    }
  );

  const { selectedItems } = collectionProps;

  useEffect(() => {
    loadItems();
    props.addNotification([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadItems() {
    getAllMaintenance().then((data) => {
      setAllItems(data);
      setTableLoading(false);
      setConfirmLoading(false);
    });
  }

  function handleDismiss() {
    setAddVisible(false);
    setDeleteVisible(false);
    setEditVisible(false);
    setMessage(DEFAULT_MESSAGE);
    setStartTimeVal(null);
    setEndTimeVal(null);
    setShowFromVal(null);
    setShowUntilVal(null);
    setMessageError("");
    setStartTimeError("");
    setEndTimeError("");
    setShowFromError("");
    setShowUntilError("");
    setConflictError("");
    setFormKey((k) => k + 1);
  }

  function handleAddOpen() {
    const defaultStartTime = moment().add(2, "weeks").toISOString();
    const defaultEndTime = moment().add(2, "weeks").add(3, "hours").toISOString();
    setStartTimeVal(defaultStartTime);
    setEndTimeVal(defaultEndTime);
    setShowFromVal(moment().toISOString());
    setShowUntilVal(defaultEndTime);
    setAddVisible(true);
  }

  function validate(excludeId = null) {
    const { valid, errors } = validateMaintenance({ message, startTimeVal, endTimeVal, showFromVal, showUntilVal });
    setMessageError(errors.message);
    setStartTimeError(errors.startTime);
    setEndTimeError(errors.endTime);
    setShowFromError(errors.showFrom);
    setShowUntilError(errors.showUntil);
    if (valid) {
      const conflict = detectMaintenanceOverlap(startTimeVal, endTimeVal, allItems, excludeId);
      if (conflict) {
        const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" });
        setConflictError(
          `Too close to existing window "${conflict.message}" (${fmt(conflict.startTime)} – ${fmt(conflict.endTime)}) — a 5-minute gap is required between maintenance windows.`
        );
        return false;
      }
    }
    setConflictError("");
    return valid;
  }

  function buildData() {
    return {
      message,
      startTime: startTimeVal,
      endTime: endTimeVal,
      showFrom: showFromVal,
      showUntil: showUntilVal,
    };
  }

  function handleSubmit() {
    if (!validate()) return;
    setConfirmLoading(true);
    const data = buildData();
    addMaintenance(data).then(async () => {
      const revokedCount = await revokeConflictingRequests(data);
      loadItems();
      handleDismiss();
      const content = revokedCount > 0
        ? `Maintenance added successfully. ${revokedCount} conflicting request${revokedCount > 1 ? "s were" : " was"} revoked.`
        : "Maintenance added successfully.";
      props.addNotification([{ type: "success", content, dismissible: true, onDismiss: () => props.addNotification([]) }]);
    });
  }

  function handleEdit() {
    const item = selectedItems[0];
    setMessage(item.message || "");
    setStartTimeVal(item.startTime || null);
    setEndTimeVal(item.endTime || null);
    setShowFromVal(item.showFrom || null);
    setShowUntilVal(item.showUntil || null);
    setFormKey((k) => k + 1);
    setEditVisible(true);
  }

  function handleConfirmEdit() {
    if (!validate(selectedItems[0].id)) return;
    setConfirmLoading(true);
    const data = { id: selectedItems[0].id, ...buildData() }
    editMaintenance(data).then(async () => {
      const revokedCount = await revokeConflictingRequests(data);
      loadItems();
      handleDismiss();
      const content = revokedCount > 0
        ? `Maintenance updated successfully. ${revokedCount} conflicting request${revokedCount > 1 ? "s were" : " was"} revoked.`
        : "Maintenance updated successfully.";
      props.addNotification([{ type: "success", content, dismissible: true, onDismiss: () => props.addNotification([]) }]);
    });
  }

  function handleDelete() {
    setConfirmLoading(true);
    Promise.all(selectedItems.map((item) => delMaintenance({ id: item.id }))).then(() => {
      loadItems();
      setDeleteVisible(false);
      props.addNotification([{ type: "success", content: "Maintenance deleted successfully", dismissible: true, onDismiss: () => props.addNotification([]) }]);
    });
  }

  function handleSelect({ detail }) {
    if (detail.id === "delete") setDeleteVisible(true);
    else handleEdit();
  }

  const formProps = {
    message, onMessageChange: (v) => { setMessageError(""); setMessage(v); }, messageError,
    startTimeVal, onStartTimeChange: (v) => {
      setStartTimeError("");
      setConflictError("");
      setStartTimeVal(v);
      if (v) {
        setShowFromError(""); setShowFromVal(moment(v).subtract(2, "weeks").toISOString());
        const autoEnd = moment(v).add(3, "hours").toISOString();
        setEndTimeError(""); setEndTimeVal(autoEnd);
        setShowUntilError(""); setShowUntilVal(autoEnd);
      }
    }, startTimeError,
    endTimeVal, onEndTimeChange: (v) => {
      setEndTimeError("");
      setEndTimeVal(v);
      if (v) { setShowUntilError(""); setShowUntilVal(v); }
    }, endTimeError,
    showFromVal, onShowFromChange: (v) => { setShowFromError(""); setShowFromVal(v); }, showFromError,
    showUntilVal, onShowUntilChange: (v) => { setShowUntilError(""); setShowUntilVal(v); }, showUntilError,
  };

  return (
    <div className="container">
      <Table
        {...collectionProps}
        resizableColumns
        loading={tableLoading}
        loadingText="Fetching maintenance entries"
        wrapLines
        header={
          <Header
            counter={selectedItems.length ? `(${selectedItems.length}/${allItems.length})` : `(${allItems.length})`}
            description="Define a maintenance window. All times are local time."
            actions={
              <SpaceBetween size="s" direction="horizontal">
                <Button iconName="refresh" onClick={loadItems} />
                <ButtonDropdown
                  items={[
                    { text: "Edit", id: "edit", disabled: selectedItems.length !== 1 },
                    { text: "Delete", id: "delete", disabled: selectedItems.length === 0 },
                  ]}
                  onItemClick={handleSelect}
                >
                  Actions
                </ButtonDropdown>
                <Button variant="primary" onClick={() => handleAddOpen()}>
                  Add Maintenance
                </Button>
              </SpaceBetween>
            }
          >
            Maintenance
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder="Find by date (YYYY-MM-DD)"
            countText={filteredItemsCount}
          />
        }
        columnDefinitions={COLUMN_DEFINITIONS}
        visibleColumns={preferences.visibleContent}
        pagination={<Pagination {...paginationProps} />}
        preferences={<MyCollectionPreferences preferences={preferences} setPreferences={setPreferences} />}
        items={items}
        selectionType="multi"
      />

      <Modal onDismiss={handleDismiss} visible={addVisible} closeAriaLabel="Close modal" size="large" header="Add Maintenance">
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDismiss}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit} loading={confirmLoading}>Add Maintenance</Button>
            </SpaceBetween>
          }
        >
          {conflictError && (
            <Alert type="error" header="Maintenance window conflict">{conflictError}</Alert>
          )}
          <MaintenanceForm key={formKey} {...formProps} />
        </Form>
      </Modal>

      <Modal
        onDismiss={() => setDeleteVisible(false)}
        visible={deleteVisible}
        closeAriaLabel="Close modal"
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDelete} loading={confirmLoading}>Confirm</Button>
            </SpaceBetween>
          </Box>
        }
        header="Delete maintenance"
      >
        Are you sure you want to delete the selected maintenance {selectedItems.length > 1 ? "entries" : "entry"}?
      </Modal>

      {selectedItems.length > 0 && (
        <Modal
          onDismiss={handleDismiss}
          visible={editVisible}
          closeAriaLabel="Close modal"
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleDismiss}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmEdit} loading={confirmLoading}>Confirm</Button>
              </SpaceBetween>
            </Box>
          }
          header="Edit Maintenance"
        >
          <SpaceBetween size="l">
            <ColumnLayout columns={2} variant="text-grid">
              <ValueWithLabel label="Id">{selectedItems[0].id}</ValueWithLabel>
            </ColumnLayout>
            {conflictError && (
              <Alert type="error" header="Maintenance window conflict">{conflictError}</Alert>
            )}
            <MaintenanceForm key={formKey} {...formProps} />
          </SpaceBetween>
        </Modal>
      )}
    </div>
  );
}

export default Maintenance;
